/**
 * MobileControls.js - Handles touch-based controls for mobile devices
 * 
 * Provides a virtual joystick for movement and buttons for attack and aim
 */

import nipplejs from 'nipplejs';
import MobileDetect from 'mobile-detect';

/**
 * Creates and manages mobile controls
 * @param {Object} options - Configuration options
 * @returns {Object} Mobile controls interface
 */
export function createMobileControls(options = {}) {
    // Default options
    const config = {
        playerController: null,
        camera: null,
        ...options
    };
    
    // Device detection
    const md = new MobileDetect(window.navigator.userAgent);
    let isMobile = md.mobile() !== null || md.tablet() !== null;
    
    // For testing on desktop, check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('forceMobile')) {
        isMobile = urlParams.get('forceMobile') === 'true';
    }
    
    // State
    let enabled = isMobile;
    let moveJoystick = null;
    let controlsContainer = null;
    
    // Forward/right movement values from joystick
    let forwardInput = 0;
    let rightInput = 0;
    
    // Button states
    let isAttacking = false;
    let isAiming = false;
    
    /**
     * Creates the DOM elements for mobile controls
     */
    function createControlElements() {
        // Main container for all controls
        controlsContainer = document.createElement('div');
        controlsContainer.id = 'mobile-controls';
        controlsContainer.style.position = 'fixed';
        controlsContainer.style.left = '0';
        controlsContainer.style.bottom = '0';
        controlsContainer.style.width = '100%';
        controlsContainer.style.height = '100%';
        controlsContainer.style.pointerEvents = 'none';
        controlsContainer.style.zIndex = '1000';
        controlsContainer.style.display = enabled ? 'block' : 'none';
        
        // Create a separate container for buttons with higher z-index
        const buttonContainer = document.createElement('div');
        buttonContainer.style.position = 'absolute';
        buttonContainer.style.left = '0';
        buttonContainer.style.top = '0';
        buttonContainer.style.width = '100%';
        buttonContainer.style.height = '100%';
        buttonContainer.style.pointerEvents = 'none';
        buttonContainer.style.zIndex = '1500'; // Higher than camera touch zone
        controlsContainer.appendChild(buttonContainer);
        
        // Joystick container
        const joystickContainer = document.createElement('div');
        joystickContainer.id = 'joystick-zone';
        joystickContainer.style.position = 'absolute';
        joystickContainer.style.left = '80px';
        joystickContainer.style.bottom = '80px';
        joystickContainer.style.width = '120px';
        joystickContainer.style.height = '120px';
        joystickContainer.style.pointerEvents = 'auto';
        joystickContainer.style.zIndex = '1600'; // Higher than button container
        buttonContainer.appendChild(joystickContainer);
        
        // Second (smaller) attack button next to joystick
        const secondaryAttackButton = document.createElement('div');
        secondaryAttackButton.id = 'secondary-attack-button';
        secondaryAttackButton.textContent = '🔫';
        secondaryAttackButton.style.position = 'absolute';
        secondaryAttackButton.style.left = '20px'; // Position on the left side
        secondaryAttackButton.style.bottom = '140px'; // Moved up by another 30px
        secondaryAttackButton.style.width = '60px'; // Smaller than main attack button
        secondaryAttackButton.style.height = '60px'; // Smaller than main attack button
        secondaryAttackButton.style.borderRadius = '50%';
        secondaryAttackButton.style.backgroundColor = 'rgba(255, 0, 0, 0.5)';
        secondaryAttackButton.style.display = 'flex';
        secondaryAttackButton.style.justifyContent = 'center';
        secondaryAttackButton.style.alignItems = 'center';
        secondaryAttackButton.style.fontSize = '20px'; // Slightly smaller font
        secondaryAttackButton.style.pointerEvents = 'auto';
        secondaryAttackButton.style.userSelect = 'none';
        secondaryAttackButton.style.zIndex = '1600'; // Higher than camera touch zone
        buttonContainer.appendChild(secondaryAttackButton);
        
        // Create attack button
        const attackButton = document.createElement('div');
        attackButton.id = 'attack-button';
        attackButton.style.cssText = `
          position: absolute;
          right: 130px;
          bottom: 100px;
          width: 80px;
          height: 80px;
          background: rgba(255, 0, 0, 0.5);
          border-radius: 50%;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1600;
          pointer-events: auto;
          user-select: none;
        `;

        // Add joystick base and stick to attack button
        const attackBase = document.createElement('div');
        attackBase.style.cssText = `
          position: absolute;
          width: 100%;
          height: 100%;
          background: rgba(255, 0, 0, 0.3);
          border-radius: 50%;
          border: 2px solid rgba(255, 255, 255, 0.5);
        `;
        attackButton.appendChild(attackBase);

        const attackStick = document.createElement('div');
        attackStick.style.cssText = `
          position: absolute;
          width: 40%;
          height: 40%;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 50%;
          border: 2px solid rgba(255, 255, 255, 0.9);
          box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
          transform: translate(-50%, -50%);
          left: 50%;
          top: 50%;
        `;
        attackButton.appendChild(attackStick);

        // Add shoot icon (using text emoji)
        const shootIcon = document.createElement('div');
        shootIcon.textContent = '🔫';
        shootIcon.style.cssText = `
          position: absolute;
          font-size: 24px;
          opacity: 0.9;
          pointer-events: none;
          line-height: 1;
          text-align: center;
        `;
        attackButton.appendChild(shootIcon);

        // Create aim button
        const aimButton = document.createElement('div');
        aimButton.id = 'aim-button';
        aimButton.style.cssText = `
          position: absolute;
          right: 30px;
          bottom: 100px;
          width: 80px;
          height: 80px;
          background: rgba(0, 0, 255, 0.5);
          border-radius: 50%;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1600;
          pointer-events: auto;
          user-select: none;
        `;

        // Add joystick base and stick to aim button
        const aimBase = document.createElement('div');
        aimBase.style.cssText = `
          position: absolute;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 255, 0.3);
          border-radius: 50%;
          border: 2px solid rgba(255, 255, 255, 0.5);
        `;
        aimButton.appendChild(aimBase);

        const aimStick = document.createElement('div');
        aimStick.style.cssText = `
          position: absolute;
          width: 40%;
          height: 40%;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 50%;
          border: 2px solid rgba(255, 255, 255, 0.9);
          box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
          transform: translate(-50%, -50%);
          left: 50%;
          top: 50%;
        `;
        aimButton.appendChild(aimStick);

        // Add aim icon (using text emoji)
        const aimIcon = document.createElement('div');
        aimIcon.textContent = '🎯';
        aimIcon.style.cssText = `
          position: absolute;
          font-size: 24px;
          opacity: 0.9;
          pointer-events: none;
          line-height: 1;
          text-align: center;
        `;
        aimButton.appendChild(aimIcon);

        // Add buttons to container
        buttonContainer.appendChild(attackButton);
        buttonContainer.appendChild(aimButton);
        buttonContainer.appendChild(secondaryAttackButton);

        // Pause button
        const pauseButton = document.createElement('div');
        pauseButton.id = 'pause-button';
        pauseButton.textContent = '⏸️';
        pauseButton.style.cssText = `
          position: absolute;
          right: 20px;
          top: 20px;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 24px;
          pointer-events: auto;
          user-select: none;
          z-index: 2000;
          border: 2px solid rgba(255, 255, 255, 0.7);
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
          cursor: pointer;
          touch-action: none;
        `;
        buttonContainer.appendChild(pauseButton);

        document.body.appendChild(controlsContainer);
        
        return {
            joystickContainer,
            attackButton,
            aimButton,
            secondaryAttackButton,
            pauseButton
        };
    }
    
    /**
     * Initialize the joystick
     * @param {HTMLElement} container - Container element for the joystick
     */
    function initializeJoystick(container) {
        moveJoystick = nipplejs.create({
            zone: container,
            mode: 'static',
            position: { left: '50%', top: '50%' },
            color: 'white',
            size: 100,
            lockX: false,
            lockY: false
        });
        
        // Handle joystick movement
        moveJoystick.on('move', (evt, data) => {
            // Calculate forward/backward and left/right values based on joystick angle
            const magnitude = Math.min(data.distance / 50, 1); // Normalize distance to 0-1
            forwardInput = Math.sin(data.angle.radian) * magnitude;
            rightInput = Math.cos(data.angle.radian) * magnitude;
            
            // Apply to player controller if available
            if (config.playerController && typeof config.playerController.setMoveInput === 'function') {
                config.playerController.setMoveInput(forwardInput, rightInput);
            }
        });
        
        // Reset on joystick release
        moveJoystick.on('end', () => {
            forwardInput = 0;
            rightInput = 0;
            
            // Apply to player controller if available
            if (config.playerController && typeof config.playerController.setMoveInput === 'function') {
                config.playerController.setMoveInput(0, 0);
            }
        });
    }
    
    /**
     * Initialize action buttons
     * @param {HTMLElement} attackBtn - Attack button element
     * @param {HTMLElement} aimBtn - Aim button element
     * @param {HTMLElement} secondaryAttackBtn - Secondary attack button element
     * @param {HTMLElement} pauseBtn - Pause button element
     */
    function initializeButtons(attackBtn, aimBtn, secondaryAttackBtn, pauseBtn) {
        // Attack button events with camera control
        let attackButtonTouchStartTime = 0;
        let isAttackDragging = false;
        let lastAttackX = 0;
        let lastAttackY = 0;
        let attackTouchId = null;
        
        attackBtn.addEventListener('touchstart', (e) => {
            // Only handle if not already tracking a touch for this button
            if (attackTouchId === null) {
                const touch = e.changedTouches[0];
                attackTouchId = touch.identifier;
                e.preventDefault();
                attackButtonTouchStartTime = performance.now();
                lastAttackX = touch.clientX;
                lastAttackY = touch.clientY;
                
                isAttacking = true;
                console.log('[MobileControls] Attack button pressed (Touch ID:', attackTouchId, ')');
                
                if (config.playerController) {
                    // Start firing weapon if it's a pistol
                    const currentWeapon = config.playerController.inventory.getEquipped();
                    if (currentWeapon && typeof currentWeapon.startFiring === 'function') {
                        console.log("[MobileControls] Starting weapon firing");
                        currentWeapon.startFiring();
                        currentWeapon.justPressed = true; // Ensure the pistol knows this is a new press
                    }
                    
                    if (typeof config.playerController.startAttack === 'function') {
                        config.playerController.startAttack();
                    }
                    if (typeof config.playerController.handleFire === 'function') {
                        console.log('[MobileControls] Triggering initial fire attempt');
                        config.playerController.handleFire();
                    }
                }
            }
        }, { passive: false });
        
        attackBtn.addEventListener('touchmove', (e) => {
            // Find the touch associated with this button
            let touch = null;
            for (let i = 0; i < e.touches.length; i++) {
                if (e.touches[i].identifier === attackTouchId) {
                    touch = e.touches[i];
                    break;
                }
            }

            if (touch) {
                e.preventDefault();
                
                const movementX = touch.clientX - lastAttackX;
                const movementY = touch.clientY - lastAttackY;
                
                const sensitivity = 8.0;
                
                if (config.camera && typeof config.camera.processMobileTouch === 'function') {
                    config.camera.processMobileTouch(movementX * sensitivity, movementY * sensitivity);
                } else if (config.playerController && typeof config.playerController.rotate === 'function') {
                    config.playerController.rotate(movementX * sensitivity, movementY * sensitivity);
                }
                
                lastAttackX = touch.clientX;
                lastAttackY = touch.clientY;
                isAttackDragging = true;

                const attackStick = attackBtn.querySelector(':scope > div:nth-child(2)'); // Get the stick element
                const rect = attackBtn.getBoundingClientRect();
                const centerX = rect.left + rect.width / 2;
                const centerY = rect.top + rect.height / 2;
                const deltaX = touch.clientX - centerX;
                const deltaY = touch.clientY - centerY;
                const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
                const maxDistance = rect.width / 2;
                
                if (attackStick && distance <= maxDistance) {
                    const angle = Math.atan2(deltaY, deltaX);
                    const moveX = Math.cos(angle) * (distance / maxDistance) * 100;
                    const moveY = Math.sin(angle) * (distance / maxDistance) * 100;
                    attackStick.style.transform = `translate(calc(-50% + ${moveX}%), calc(-50% + ${moveY}%))`;
                }
            }
        }, { passive: false });
        
        attackBtn.addEventListener('touchend', (e) => {
            // Check if the ended touch is the one we were tracking
            let touchEnded = false;
            for (let i = 0; i < e.changedTouches.length; i++) {
                if (e.changedTouches[i].identifier === attackTouchId) {
                    touchEnded = true;
                    break;
                }
            }

            if (touchEnded) {
                e.preventDefault();
                
                const touchDuration = performance.now() - attackButtonTouchStartTime;
                if (touchDuration < 200 && !isAttackDragging) {
                    if (config.playerController && typeof config.playerController.handleFire === 'function') {
                        config.playerController.handleFire();
                    }
                }
                
                isAttacking = false;
                console.log('[MobileControls] Attack button released (Touch ID:', attackTouchId, ')');
                
                if (config.playerController) {
                    // Stop firing weapon if it's a pistol
                    const currentWeapon = config.playerController.inventory.getEquipped();
                    if (currentWeapon && typeof currentWeapon.stopFiring === 'function') {
                        console.log("[MobileControls] Stopping weapon firing");
                        currentWeapon.stopFiring();
                    }
                    
                    if (typeof config.playerController.stopAttack === 'function') {
                        config.playerController.stopAttack();
                    }
                }
                
                isAttackDragging = false;
                const attackStick = attackBtn.querySelector(':scope > div:nth-child(2)'); // Get the stick element
                if (attackStick) attackStick.style.transform = 'translate(-50%, -50%)';
                attackTouchId = null; // Release the touch ID
            }
        });

        // Secondary attack button events
        if (secondaryAttackBtn) {
            secondaryAttackBtn.addEventListener('touchstart', (e) => {
                e.preventDefault();
                isAttacking = true; // Set flag for game loop
                console.log('[MobileControls] Secondary attack button pressed');
                
                if (config.playerController) {
                    // Start the attack sequence (sets pistol state)
                    if (typeof config.playerController.startAttack === 'function') {
                        config.playerController.startAttack();
                    }
                    // Immediately attempt the first shot
                    if (typeof config.playerController.handleFire === 'function') {
                        console.log('[MobileControls] Triggering initial fire attempt from secondary button');
                        config.playerController.handleFire();
                    } else {
                        console.warn('[MobileControls] Player controller handleFire method not available');
                    }
                } else {
                    console.warn('[MobileControls] Player controller not available for attack');
                }
            });
            
            secondaryAttackBtn.addEventListener('touchend', (e) => {
                e.preventDefault();
                isAttacking = false; // Clear flag for game loop
                console.log('[MobileControls] Secondary attack button released');
                
                if (config.playerController && typeof config.playerController.stopAttack === 'function') {
                    config.playerController.stopAttack();
                } else {
                    console.warn('[MobileControls] Player controller or stopAttack method not available');
                }
            });
        }
        
        // Aim button events
        let aimButtonTouchStartTime = 0;
        let isAimDragging = false;
        let lastAimX = 0;
        let lastAimY = 0;
        let aimTouchId = null;
        
        aimBtn.addEventListener('touchstart', (e) => {
             // Only handle if not already tracking a touch for this button
            if (aimTouchId === null) {
                const touch = e.changedTouches[0];
                aimTouchId = touch.identifier;
                e.preventDefault();
                aimButtonTouchStartTime = performance.now();
                lastAimX = touch.clientX;
                lastAimY = touch.clientY;
                
                console.log('[MobileControls] Aim button pressed (Touch ID:', aimTouchId, ')');
                if (config.playerController && typeof config.playerController.startAiming === 'function') {
                    config.playerController.startAiming();
                }
            }
        }, { passive: false });
        
        aimBtn.addEventListener('touchmove', (e) => {
            // Find the touch associated with this button
            let touch = null;
            for (let i = 0; i < e.touches.length; i++) {
                if (e.touches[i].identifier === aimTouchId) {
                    touch = e.touches[i];
                    break;
                }
            }

            if (touch) {
                e.preventDefault();
                
                const movementX = touch.clientX - lastAimX;
                const movementY = touch.clientY - lastAimY;
                
                const sensitivity = 8.0;
                
                if (config.camera && typeof config.camera.processMobileTouch === 'function') {
                    config.camera.processMobileTouch(movementX * sensitivity, movementY * sensitivity);
                } else if (config.playerController && typeof config.playerController.rotate === 'function') {
                    config.playerController.rotate(movementX * sensitivity, movementY * sensitivity);
                }
                
                lastAimX = touch.clientX;
                lastAimY = touch.clientY;
                isAimDragging = true;

                const aimStick = aimBtn.querySelector(':scope > div:nth-child(2)'); // Get the stick element
                const rect = aimBtn.getBoundingClientRect();
                const centerX = rect.left + rect.width / 2;
                const centerY = rect.top + rect.height / 2;
                const deltaX = touch.clientX - centerX;
                const deltaY = touch.clientY - centerY;
                const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
                const maxDistance = rect.width / 2;
                
                if (aimStick && distance <= maxDistance) {
                    const angle = Math.atan2(deltaY, deltaX);
                    const moveX = Math.cos(angle) * (distance / maxDistance) * 100;
                    const moveY = Math.sin(angle) * (distance / maxDistance) * 100;
                    aimStick.style.transform = `translate(calc(-50% + ${moveX}%), calc(-50% + ${moveY}%))`;
                }
            }
        }, { passive: false });
        
        aimBtn.addEventListener('touchend', (e) => {
             // Check if the ended touch is the one we were tracking
            let touchEnded = false;
            for (let i = 0; i < e.changedTouches.length; i++) {
                if (e.changedTouches[i].identifier === aimTouchId) {
                    touchEnded = true;
                    break;
                }
            }

            if (touchEnded) {
                e.preventDefault();
                
                const touchDuration = performance.now() - aimButtonTouchStartTime;
                if (touchDuration < 200 && !isAimDragging) {
                    isAiming = !isAiming;
                    if (config.playerController) {
                        if (isAiming) {
                            config.playerController.startAiming();
                        } else {
                            config.playerController.stopAiming();
                        }
                    }
                } else {
                    isAiming = false;
                    if (config.playerController && typeof config.playerController.stopAiming === 'function') {
                        config.playerController.stopAiming();
                    }
                }
                
                console.log('[MobileControls] Aim button released (Touch ID:', aimTouchId, ')');
                isAimDragging = false;
                const aimStick = aimBtn.querySelector(':scope > div:nth-child(2)'); // Get the stick element
                if (aimStick) aimStick.style.transform = 'translate(-50%, -50%)';
                aimTouchId = null; // Release the touch ID
            }
        });

        // Pause button events
        if (pauseBtn) {
            pauseBtn.addEventListener('touchstart', (e) => {
                e.preventDefault();
                if (window.togglePauseState) {
                    window.togglePauseState();
                }
            }, { passive: false });
        } else {
            console.warn('[MobileControls] Pause button element not found during initialization.');
        }
    }
    
    /**
     * Initialize right side touch area for camera control
     */
    function initializeCameraControl() {
        // Create a touch zone for camera control on the right side
        const touchZone = document.createElement('div');
        touchZone.id = 'camera-touch-zone';
        touchZone.style.position = 'absolute';
        touchZone.style.right = '0';
        touchZone.style.left = '0'; // Add left: 0 to ensure full width
        touchZone.style.top = '115px'; // Moved down 15px from 100px
        touchZone.style.width = '100%'; // Changed from 50% to 100%
        touchZone.style.height = 'calc(70% - 100px)'; // Adjust height to not overlap with buttons
        touchZone.style.pointerEvents = 'auto';
        touchZone.style.zIndex = '1450'; // Increase z-index slightly
        controlsContainer.appendChild(touchZone);
        
        // Add a small debug overlay to show touch zone (can remove later)
        touchZone.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
        touchZone.style.border = '1px solid rgba(255, 255, 255, 0.2)';
        
        let lastX = 0;
        let lastY = 0;
        let cameraTouchId = null; 
        
        touchZone.addEventListener('touchstart', (e) => {
            // Only track if not already tracking
            if (cameraTouchId === null) {
                // Check if touch is within the zone's bounds (optional, but good practice)
                const touch = e.changedTouches[0];
                const rect = touchZone.getBoundingClientRect();
                if (touch.clientX >= rect.left && touch.clientX <= rect.right &&
                    touch.clientY >= rect.top && touch.clientY <= rect.bottom) 
                {
                    cameraTouchId = touch.identifier;
                    e.preventDefault(); 
                    lastX = touch.clientX;
                    lastY = touch.clientY;
                    console.log('[MobileControls] Camera touch started (Touch ID:', cameraTouchId, ')');
                }
            }
        }, { passive: false });
        
        touchZone.addEventListener('touchmove', (e) => {
            let touch = null;
            for (let i = 0; i < e.touches.length; i++) {
                if (e.touches[i].identifier === cameraTouchId) {
                    touch = e.touches[i];
                    break;
                }
            }

            if (touch) {
                e.preventDefault();
                
                const movementX = touch.clientX - lastX;
                const movementY = touch.clientY - lastY;
                
                const sensitivity = 8.0;
                
                if (config.camera && typeof config.camera.processMobileTouch === 'function') {
                    config.camera.processMobileTouch(movementX * sensitivity, movementY * sensitivity);
                } else if (config.playerController && typeof config.playerController.rotate === 'function') {
                    config.playerController.rotate(movementX * sensitivity, movementY * sensitivity);
                }
                
                lastX = touch.clientX;
                lastY = touch.clientY;
            }
        }, { passive: false });

        touchZone.addEventListener('touchend', (e) => {
            let touchEnded = false;
            for (let i = 0; i < e.changedTouches.length; i++) {
                if (e.changedTouches[i].identifier === cameraTouchId) {
                    touchEnded = true;
                    break;
                }
            }

            if (touchEnded) {
                e.preventDefault();
                console.log('[MobileControls] Camera touch ended (Touch ID:', cameraTouchId, ')');
                cameraTouchId = null; // Release the touch ID
            }
        });
    }
    
    /**
     * Initialize mobile controls
     */
    function initialize() {
        // Only initialize if enabled
        if (!enabled) return;
        
        console.log('[MobileControls] Initializing mobile controls');
        
        // Create DOM elements
        const elements = createControlElements();
        
        // Initialize joystick
        initializeJoystick(elements.joystickContainer);
        
        // Initialize buttons with all elements including pause button
        initializeButtons(
            elements.attackButton, 
            elements.aimButton, 
            elements.secondaryAttackButton,
            elements.pauseButton
        );
        
        // Initialize camera controls
        initializeCameraControl();
        
        // Block touchmove events that would scroll the page
        document.addEventListener('touchmove', (e) => {
            if (e.target.closest('#mobile-controls')) {
                e.preventDefault();
            }
        }, { passive: false });
        
        console.log('[MobileControls] Mobile controls initialized');
    }
    
    /**
     * Set the player controller reference
     * @param {Object} playerController - The player controller instance
     */
    function setPlayerControllerReference(playerController) {
        if (!playerController) return;
        
        config.playerController = playerController;
        console.log('[MobileControls] Player controller reference updated');
    }
    
    /**
     * Set the camera reference
     * @param {Object} camera - The camera controller instance
     */
    function setCameraReference(camera) {
        if (!camera) return;
        
        config.camera = camera;
        console.log('[MobileControls] Camera reference updated');
    }
    
    /**
     * Enable or disable mobile controls
     * @param {boolean} state - Whether to enable mobile controls
     */
    function setEnabled(state) {
        enabled = state;
        
        if (controlsContainer) {
            controlsContainer.style.display = enabled ? 'block' : 'none';
        }
    }
    
    /**
     * Update mobile controls
     * Called every frame
     */
    function update() {
        // Only update if enabled
        if (!enabled) return;
        
        // Apply continuous inputs if needed
        if (config.playerController) {
            // Movement already handled by joystick events
            
            // Update aiming state if needed
            if (isAiming && typeof config.playerController.updateAiming === 'function') {
                config.playerController.updateAiming();
            }

            // Handle continuous fire for level 5+ pistols
            if (isAttacking) {
                const currentWeapon = config.playerController.inventory.getEquipped();
                if (currentWeapon && currentWeapon.name === "Pistol" && currentWeapon.level >= 5) {
                    config.playerController.handleFire();
                }
            }
        }
    }
    
    /**
     * Clean up mobile controls
     */
    function cleanup() {
        if (moveJoystick) {
            moveJoystick.destroy();
        }
        
        if (controlsContainer && controlsContainer.parentNode) {
            controlsContainer.parentNode.removeChild(controlsContainer);
        }
    }
    
    // Return public interface
    return {
        initialize,
        update,
        cleanup,
        setEnabled,
        setPlayerControllerReference,
        setCameraReference,
        isEnabled: () => enabled,
        isMobileDevice: () => isMobile
    };
} 